import React from "react";
import { useState, useEffect } from "react";
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import {
  getInitialData,
  getProducts,
  getProductById,
  getVariations,
  getPizzaToppingsPrices,
  getVariationConfig,
  getRestaurantTime,
  getSplDiscs,
} from "./apis/RangerOnlineApi";

import { NavbarTableOrder } from "./components/NavbarTableOrder";
import { TableOrderContent } from "./components/TableOrderContent";
import { AlertPopUp } from "./components/AlertPopUp";
import { OrderTypeDialog } from "./dialogs/OrderTypeDialog";
import { DialogProduct } from "./components/DialogProduct";
import { DealDialog } from "./dialogs/DealDialog";
import { PizzaDialog } from "./dialogs/PizzaDialog";
import { CYOPizzaDialog } from "./dialogs/CYOPizzaDialog";
import { IdleAlertPopUp } from "./components/IdleAlertPopUp";
import { CheckOutDialogTableOrder } from "./dialogs/CheckOutDialogTableOrder";
import { TopBar } from "./components/TopBar";
import { SideBar } from "./components/SideBar";
import { getPromoCode } from "./apis/RangerOnlineApi";
import { RightBar } from "./components/RightBar";
import { handleItemSelection } from "./Utils/utils";
import { TableOrderTypeDialog } from "./dialogs/TableOrderTypeDialog";
import Confirmation from "./components/Confirmation";
import moment from "moment";

const darkTheme = createTheme({
  typography: {
    fontFamily: `Roboto, Sans-serif`,
    fontColor: "#972020",
    h6: {
      fontSize: "1.2rem",
    },
    body1: {
      fontWeight: "550",
    },
    body2: {
      fontWeight: "550",
      fontSize: "1rem",
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#972020",
    },
    background: {
      default: "#fafafa",
      paper: "#fafafa",
    },
    text: {
      primary: "#532A0C",
    },
  },
  components: {
    MuiStepIcon: {
      styleOverrides: {
        root: {
          "&.Mui-completed": {
            color: "#5BB318",
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        root: {
          "&.MuiStepLabel-label": {
            fontWeight: "600",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          "&.MuiCard-root": {
            boxShadow: `3px 2px #F0EAD6`,
            border: "solid",
            borderWidth: "2px",
            borderColor: "#fff",
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          "&.MuiCardHeader-title": {
            color: "#972020",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          "&.MuiTableCell-root": {
            padding: "5px",
            fontWeight: "550",
          },
        },
      },
    },
    MuiTableFooter: {
      styleOverrides: {
        root: {
          "&.MuiTableFooter-root": {
            backgroundColor: "#fafafa",
            position: "sticky",
            insetBlockEnd: "0",
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          "&.MuiAppBar-root": {
            boxShadow: "0px 3px 3px -1px #972020",
            width: "100% !important",
            zIndex: "1250",
            position: "sticky",
            height: "55px",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: "#fafafa",
        },
      },
    },
    MuiTypography: {
      styleOverrides: {},
    },
    MuiTextField: {
      borderRadius: "30px",
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "550",
          fontSize: "1rem",
        },
      },
    },
  },
});

let product = {};
let menuDirection = "left";

let defaultsType = {
  taxes: [],
  categories: [],
  settings: { menuDirection: "Left", EnableGiftcardPromo: "0" },
  companyData: {},
};

function App() {
  const [defaults, setDefaults] = useState({});
  const [sideBar, setSideBar] = useState(true);
  const [products, setProducts] = useState([]);
  const [variationConfig, setVariationConfig] = useState({});
  const [variations, setVariations] = useState([]);
  const [loader, setLoader] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const [orderType, setOrderType] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [orderItemIndex, setOrderItemIndex] = useState(0);
  const [orderTypeDialog, setOrderTypeDialog] = useState(false);
  const [steps, setSteps] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [showIdleDialog, setShowIdleDialog] = useState(false);
  const [checkOutOpen, setCheckOutOpen] = useState(false);
  const [order, setOrder] = useState({});
  const [detailId, setDetailId] = useState(0);
  const [promoCode, setPromoCode] = useState({});
  const [selectedCat, setSelectedCat] = useState();
  const [selectedProd, setSelectedProd] = useState([]);
  const [orderTypePopUp, setOrderTypePopUp] = useState(false);
  const [isAvailablePopUp, setIsAvailablePopUp] = useState(false);
  const [restaurantTime, setRestaurantTime] = useState();

  const handleAlert = (show) => {
    setShowAlert(show);
  };

  const getMaxLevel = (options, key) => {
    const maxLevel = Math.max(...options.map((x) => x[key]));
    return maxLevel;
  };

  const getCurrentLevelConfig = (level, configuration) => {
    if (configuration) {
      const maxQuantity = configuration["qtyLevel" + level];
      const forcedOption = configuration["mandatoryLevel" + level];
      const levelName = configuration["nameLevel" + level];

      return {
        maxQuantity: maxQuantity,
        forcedOption: forcedOption,
        levelName: levelName,
      };
    }
    return {};
  };

  const getSteps = (maxLevel, configuration) => {
    let steps = [];
    for (let i = 1; i <= maxLevel; i++) {
      const currentLevelConfig = getCurrentLevelConfig(i, configuration);
      steps.push({
        level: i,
        label: `${currentLevelConfig.levelName}`,
        componentType: "selection",
        currentLevelConfig,
      });
    }
    steps.push({
      level: maxLevel + 1,
      label: `Quantity`,
      componentType: "quantity",
    });
    return steps;
  };

  const handleCategoryClick = async (id) => {
    setLoader(true);
    setSelectedCat(id);
    const result = await getProducts(id);
    if (result.status == 404) {
      setProducts([]);
    } else if (result.status) {
      setShowAlert(true);
      setAlertContent(
        "An error occured while trying to find products please try again. If the error persists, contact the support team."
      );
    } else {
      setProducts(result);
    }

    // if (menuDirection == "Left") {
    //   setSideBar(false);
    // }

    setLoader(false);
  };

  const handleDialogOpen = async (
    id,
    isEdit,
    index = -1,
    idDetail = -1,
    currentQuantity
  ) => {
    setLoader(true);
    let newProduct = {};
    let quantity = 1;
    if (isEdit) {
      newProduct = await getProductById(id);
    } else {
      newProduct = { ...products.find((x) => x.productId === id) };
    }
    const result = await getVariations(newProduct.productId, 0);
    let levelMax = 0;
    let configuration;

    if (
      newProduct.validateMinQtySell &&
      newProduct.validateMinQtySell > 0 &&
      newProduct.minQtySell > 0
    ) {
      quantity = newProduct?.minQtySell || 0;
    }

    Object.assign(newProduct, {
      totalPrice:
        defaults?.settings?.ApplyTakeOutDeliveryPrice === "1"
          ? orderType === "Delivery" && newProduct?.deliveryprice
            ? parseFloat(newProduct?.deliveryprice)
            : orderType === "TakeOut" && newProduct?.takeoutprice
            ? parseFloat(newProduct?.takeoutprice)
            : parseFloat(newProduct?.price)
          : parseFloat(newProduct?.price),
      quantity: currentQuantity > 0 ? currentQuantity : quantity,
      notes: "",
    });

    if (newProduct.isPizza == 1 && newProduct.isCreateYourOwn == 1) {
      const response = await getPizzaToppingsPrices(newProduct.productId);
      if (response?.status) {
      } else {
        newProduct.pizzaToppingPrices = response;
      }
    }

    if (result && result.length > 0) {
      setVariations(result);
      configuration = await getVariationConfig(newProduct.productId);
      setVariationConfig(configuration);
      levelMax = getMaxLevel(result, "level");
    }

    if (idDetail > 0) {
      setDetailId(idDetail);
    }

    if (index >= 0) {
      setIsEdit(isEdit);
      setOrderItemIndex(index);
    }

    const newSteps = getSteps(levelMax, configuration);
    setSteps(newSteps);
    setLoader(false);

    product = newProduct;
    if (selectedProd.length === 0) {
      setOrderTypePopUp(true);
    } else {
      setDialogOpen(true);
    }
  };

  const handlePopUp = (msg, closeOnTimer) => {
    setAlertContent(msg);
    setShowAlert(true);
  };

  const handlePopUpClose = () => {
    setShowAlert(false);
  };

  const handleIdlePopUp = (errorMsg) => {
    setMessage(errorMsg);
    setShowIdleDialog(true);
  };

  const handleIdlePopUpClose = () => {
    setShowIdleDialog(false);
  };

  const handleOrderTypeSelection = (orderType) => {
    setOrderType(orderType);
    setOrderTypeDialog(false);
  };

  const handleDialogClose = () => {
    setIsEdit(false);
    setDialogOpen(false);
    product = {};
    setVariationConfig({});
    setVariations([]);
    setSteps([]);
  };

  const handleAddProduct = (newProduct) => {
    // if (selectedProd.length === 0) {
    //   Object.assign(newProduct, {
    //     totalPrice:
    //       defaults?.settings?.ApplyTakeOutDeliveryPrice === "1"
    //         ? orderType === "Delivery" && newProduct?.deliveryprice
    //           ? parseFloat(newProduct?.deliveryprice)
    //           : orderType === "TakeOut" && newProduct?.takeoutprice
    //           ? parseFloat(newProduct?.takeoutprice)
    //           : parseFloat(newProduct?.price)
    //         : parseFloat(newProduct?.price),
    //     notes: "",
    //   });
    // }
    if (newProduct.quantity > 0) {
      let ordersCopy = [...orders];
      if (isEdit) {
        ordersCopy[orderItemIndex] = newProduct;
        if (detailId > 0) {
          ordersCopy[orderItemIndex].id = detailId;
          ordersCopy[orderItemIndex].status = "modified";
        }
      } else {
        newProduct.status = "new";
        if (
          ordersCopy.filter(
            (data) => data.fullDescription === newProduct.fullDescription
          ).length > 0 &&
          defaults?.settings?.OnlineCombineLineQty == 1
        ) {
          let newArr = ordersCopy.map((item) =>
            item.fullDescription === newProduct.fullDescription
              ? {
                  ...item,
                  quantity: item.quantity + newProduct.quantity,
                }
              : item
          );
          ordersCopy = newArr;
        } else {
          ordersCopy.push(newProduct);
        }
      }

      // if (ordersCopy.filter((x) => x.isBag === 1).length === 0) {
      //   Object.assign(defaults.bagProduct, {
      //     totalPrice: parseFloat(defaults?.bagProduct?.price),
      //     quantity: 1,
      //     notes: "",
      //   });
      //   ordersCopy.push(handleItemSelection(defaults.bagProduct));
      // }

      product = {};
      setVariations([]);
      setOrders(ordersCopy);
      setSelectedProd([...selectedProd, newProduct.productId]);
      setDetailId(0);
    } else {
      alert("Please set a quantity.");
    }
  };

  const handleRemoveProduct = (index) => {
    const ordersCopy = [...orders];
    if (ordersCopy[index].id > 0) {
      ordersCopy[
        index
      ].fullDescription = `${ordersCopy[index].fullDescription} \n (VOID)`;
      ordersCopy[index].price = 0;
      ordersCopy[index].totalUnitPrice = 0;
      ordersCopy[index].totalPrice = 0;
      ordersCopy[index].status = "removed";
    } else {
      ordersCopy.splice(index, 1);
    }

    setOrders(ordersCopy);
    setSelectedProd(ordersCopy.map((item) => item.productId));
  };
  const handlePromoCode = async (promoCode) => {
    const promo = await getPromoCode(promoCode);

    if (promo?.status) {
      alert("Invalid Code");
      return;
    }

    setPromoCode(promo);
  };

  useEffect(() => {
    setLoader(true);
    getInitialData().then((x) => {
      if (x?.status) {
        handleAlert(
          true,
          x?.title
            ? x.title
            : "A communication error occured, please contact the support team."
        );
      } else {
        let defaultsCopy = { ...defaults };
        const firstCategory = x.categories[0];
        handleCategoryClick(firstCategory?.id);
        defaultsCopy.categories = x.categories;
        defaultsCopy.companyData = x.companyData;
        defaultsCopy.settings = x.settings;
        defaultsCopy.taxes = x.taxes;
        defaultsCopy.choiceItemsCategories = x.choiceItemsCategories;
        defaultsCopy.bagProduct = x.bagProduct;
        defaultsCopy.serviceCharges = x.serviceCharges;
        setDefaults(defaultsCopy);

        setLoader(false);
      }
    });
    getSplDiscs().then((x) => {
      console.log(x);
    });
  }, []);

  menuDirection = defaults?.settings?.menuDirection || "left";

  const handleOrderType = (type) => {
    setOrderType(type);
    getRestaurantTime(type).then((x) => {
      setRestaurantTime(x);
      let currentTime = new Date().getTime();
      let startTime = new Date(x.startTime).getTime();
      let endTime = new Date(x.endTime).getTime();
      console.log(
        "Start:" + startTime + ",Current:" + currentTime + ",End :" + endTime
      );
      if (!x.openstatus) {
        setIsAvailablePopUp(true);
      }
      if (x.openstatus && (currentTime < startTime || endTime < currentTime)) {
        setIsAvailablePopUp(true);
      }
    });
    setOrderTypePopUp(false);
    setDialogOpen(true);
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <NavbarTableOrder
        open={sideBar}
        menuDirection={menuDirection}
        showLogo={defaults?.settings?.ShowLogo || "0"}
        showCompanyName={defaults?.settings?.ShowCompanyName || "1"}
        itemCount={
          orders.filter(
            (x) => !(x?.fullDescription.includes("VOID") && !x.status)
          ).length
        }
        onCartClick={() =>
          orders.length > 0 ? setCheckOutOpen(true) : setCheckOutOpen(false)
        }
        // onDrawerOpen={() => setSideBar(true)}
        companyName={
          defaults?.companyData?.companyName
            ? defaults.companyData.companyName
            : ""
        }
        website={
          defaults?.companyData?.mainWebSiteUrl
            ? defaults.companyData.mainWebSiteUrl
            : ""
        }
      />

      <TopBar
        categories={defaults?.categories ? defaults.categories : []}
        onOptionClick={handleCategoryClick}
        selectedCat={selectedCat}
      />
      <Box sx={{ display: "flex" }}>
        {menuDirection == "Left" ? (
          <SideBar
            open={sideBar}
            //  onDrawerClose={() => setSideBar(false)}
            categories={defaults?.categories ? defaults.categories : []}
            onOptionClick={handleCategoryClick}
            selectedCat={selectedCat}
          />
        ) : null}

        <TableOrderContent
          open={sideBar}
          products={products}
          onOpen={handleDialogOpen}
          menuDirection={menuDirection}
          selectedProd={selectedProd}
          ApplyTakeOutDeliveryPrice={
            defaults?.settings?.ApplyTakeOutDeliveryPrice
              ? defaults?.settings?.ApplyTakeOutDeliveryPrice
              : "0"
          }
          orderType={orderType}
        />

        <RightBar
          menuDirection={menuDirection}
          taxes={defaults?.taxes ? defaults?.taxes : []}
          orders={orders}
          open={checkOutOpen}
          orderType={orderType}
          onRemove={handleRemoveProduct}
          onEdit={handleDialogOpen}
          onClose={() => setCheckOutOpen(false)}
          showLoader={() => setLoader(true)}
          closeLoader={() => setLoader(false)}
          onPopUp={handlePopUp}
          onIdlePopUp={handleIdlePopUp}
          enablePromoCode={
            defaults?.settings?.EnableGiftcardPromo
              ? defaults.settings.EnableGiftcardPromo
              : 0
          }
          onOrderSelection={handleOrderTypeSelection}
          companyName={
            defaults?.companyData?.companyName
              ? defaults.companyData.companyName
              : ""
          }
          companyData={defaults?.companyData ? defaults.companyData : {}}
          pickUpTime={
            defaults?.companyData?.pickUpTime
              ? defaults.companyData.pickUpTime
              : ""
          }
          deliveryTime={
            defaults?.companyData?.deliveryTime
              ? defaults.companyData.deliveryTime
              : ""
          }
          editOrder={order}
          onPromoCode={handlePromoCode}
          promoCode={promoCode}
          choiceItemsCategories={defaults.choiceItemsCategories || []}
          onProductClick={handleDialogOpen}
          serviceCharges={defaults.serviceCharges || []}
          settings={defaults?.settings || {}}
          deliveryCharge={
            defaults?.settings?.ApplyDeliveryChargesAsKm == 1
              ? order?.deliveryCharge
              : defaults?.companyData?.deliveryCharge
          }
        />
        {showAlert ? (
          <AlertPopUp
            show={showAlert}
            message={alertContent}
            onClose={handlePopUpClose}
            closeOnTimer={false}
            menuDirection={menuDirection}
          />
        ) : null}

        <OrderTypeDialog
          openDialog={orderTypeDialog}
          onOrderSelection={handleOrderTypeSelection}
          companyName={
            defaults?.companyData?.companyName
              ? defaults.companyData.companyName
              : ""
          }
          pickUpTime={
            defaults?.companyData?.pickUpTime
              ? defaults.companyData.pickUpTime
              : ""
          }
          deliveryTime={
            defaults?.companyData?.deliveryTime
              ? defaults.companyData.deliveryTime
              : ""
          }
          settings={
            defaults?.companyData?.settings ? defaults.companyData.settings : {}
          }
          setSelectedProd={setSelectedProd}
          typesettings={defaults?.settings || {}}
        />

        {dialogOpen && product.isPizza == 0 ? (
          <DialogProduct
            onClose={handleDialogClose}
            onProductAdd={handleAddProduct}
            product={product}
            open={dialogOpen}
            variations={variations}
            variationConfig={variationConfig}
            steps={steps}
            onPopUp={handlePopUp}
            onIdlePopUp={handleIdlePopUp}
            message={message}
            settings={defaults?.settings || {}}
          />
        ) : dialogOpen &&
          product.isPizza == 1 &&
          product.isDeal == 1 &&
          product.isCreateYourOwn != 1 ? (
          <DealDialog
            open={dialogOpen}
            onClose={handleDialogClose}
            product={product}
            variations={variations}
            variationConfig={variationConfig}
            steps={steps}
            showLoader={() => setLoader(true)}
            closeLoader={() => setLoader(false)}
            onProductAdd={handleAddProduct}
          />
        ) : dialogOpen &&
          product.isPizza == 1 &&
          product.isCreateYourOwn != 1 ? (
          <PizzaDialog
            open={dialogOpen}
            product={product}
            onProductAdd={handleAddProduct}
            onClose={handleDialogClose}
            showLoader={() => setLoader(true)}
            closeLoader={() => setLoader(false)}
            settings={defaults?.settings || {}}
          />
        ) : dialogOpen &&
          product.isPizza == 1 &&
          product.isCreateYourOwn == 1 ? (
          <CYOPizzaDialog
            open={dialogOpen}
            product={product}
            onProductAdd={handleAddProduct}
            onClose={handleDialogClose}
            showLoader={() => setLoader(true)}
            closeLoader={() => setLoader(false)}
          />
        ) : null}

        {showIdleDialog ? (
          <IdleAlertPopUp
            show={showIdleDialog}
            message={message}
            onClose={handleIdlePopUpClose}
          />
        ) : null}

        {orderTypePopUp ? (
          <TableOrderTypeDialog
            openDialog={orderTypePopUp}
            handleOrderType={handleOrderType}
            typesettings={defaults?.settings || {}}
          />
        ) : null}

        {isAvailablePopUp ? (
          <Confirmation
            confirmPopUp={isAvailablePopUp}
            title="Attention"
            message={
              restaurantTime && restaurantTime.openstatus
                ? `The store timing is ${moment(
                    restaurantTime.startTime
                  )?.format("hh:mm A")} - ${moment(
                    restaurantTime.endTime
                  )?.format("hh:mm A")}.Do you still want to continue?`
                : `Restaurant is closed today.Sorry for the inconvenience`
            }
            handleNavigation={() => {
              !restaurantTime.openstatus && window.location.reload();
              setIsAvailablePopUp(false);
            }}
            onClose={() => {
              setIsAvailablePopUp(false);
              window.location.reload();
            }}
            closeOnTimer={false}
          />
        ) : null}

        {checkOutOpen ? (
          <CheckOutDialogTableOrder
            taxes={defaults.taxes}
            orders={orders}
            open={checkOutOpen}
            orderType={orderType}
            onRemove={handleRemoveProduct}
            onEdit={handleDialogOpen}
            onClose={() => setCheckOutOpen(false)}
            showLoader={() => setLoader(true)}
            closeLoader={() => setLoader(false)}
            onPopUp={handlePopUp}
            onIdlePopUp={handleIdlePopUp}
            enablePromoCode={defaults.settings.EnableGiftcardPromo}
            deliveryCharge={
              defaults.settings.ApplyDeliveryChargesAsKm == 1
                ? order?.deliveryCharge
                : defaults.companyData.deliveryCharge
            }
            onOrderSelection={handleOrderTypeSelection}
            companyName={
              defaults?.companyData?.companyName
                ? defaults.companyData.companyName
                : ""
            }
            pickUpTime={
              defaults?.companyData?.pickUpTime
                ? defaults.companyData.pickUpTime
                : ""
            }
            deliveryTime={
              defaults?.companyData?.deliveryTime
                ? defaults.companyData.deliveryTime
                : ""
            }
            editOrder={order}
            onPromoCode={handlePromoCode}
            promoCode={promoCode}
            choiceItemsCategories={defaults.choiceItemsCategories || []}
            serviceCharges={defaults.serviceCharges || []}
            settings={defaults?.settings || {}}
            ApplyTakeOutDeliveryPrice={
              defaults?.settings?.ApplyTakeOutDeliveryPrice
                ? defaults?.settings?.ApplyTakeOutDeliveryPrice
                : "0"
            }
            companyData={defaults?.companyData ? defaults.companyData : {}}
          />
        ) : null}

        {loader ? (
          <Backdrop sx={{ color: "#fff", zIndex: "99999" }} open={loader}>
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : null}
      </Box>
    </ThemeProvider>
  );
}

export default App;
